<template>
  <Teleport
    to="#app"
    :disabled="!is_expanded"
  >
    <div
      v-if="should_show_chatbot"
      class="chatbot-element"
      :class="{ 'chatbot-element--expanded': is_expanded }"
      :style="is_expanded ? undefined : chatbot_position_style"
    >
      <div
        v-if="is_expanded"
        class="chatbot-expanded-backdrop"
        @click="onCollapse"
      />

      <div
        v-if="is_open"
        class="chatbot-panel"
        :class="{ 'chatbot-panel--expanded': is_expanded }"
        :style="chatbot_content_style"
      >
        <header class="chatbot-panel-header">
          <div class="chatbot-panel-header-left">
            <div class="chatbot-panel-header-title nds-text-p-bold">
              {{ chatbot_title }}
            </div>
          </div>

          <div class="chatbot-panel-header-actions">
            <button
              type="button"
              class="chatbot-icon-button"
              :title="$t('new_chat')"
              :aria-label="$t('new_chat')"
              :disabled="is_streaming"
              @click="onNewConversation"
            >
              <svg-element
                icon="refresh"
                size="16"
              />
            </button>

            <button
              v-if="show_history_toggle"
              type="button"
              class="chatbot-header-action"
              :class="{ 'chatbot-header-action--active': show_conversations }"
              @click="onToggleConversations"
            >
              <svg-element
                :icon="show_conversations ? 'question' : 'list'"
                size="14"
              />
              <span class="nds-text-button-small-bold">
                {{ show_conversations ? $t('conversation') : $t('history_title') }}
              </span>
            </button>

            <button
              type="button"
              class="chatbot-icon-button"
              :title="is_expanded ? $t('collapse') : $t('expand')"
              :aria-label="is_expanded ? $t('collapse') : $t('expand')"
              @click="onToggleExpand"
            >
              <svg-element
                :icon="is_expanded ? 'collapse' : 'expand'"
                size="16"
              />
            </button>
          </div>
        </header>

        <div class="chatbot-panel-body">
          <div
            v-if="show_history_panel"
            class="chatbot-history-menu"
            :class="{ 'chatbot-history-menu--sidebar': is_expanded && !is_mobile_viewport }"
          >
            <div class="chatbot-history-menu-header">
              <div class="nds-text-p-bold">
                {{ $t('history_title') }}
              </div>

              <button
                v-if="show_history_toggle"
                type="button"
                class="chatbot-icon-button"
                :title="$t('close')"
                :aria-label="$t('close')"
                @click="onCloseConversations"
              >
                <svg-element
                  icon="close"
                  size="16"
                />
              </button>
            </div>

            <div
              v-if="!is_loading_conversations && conversations.length"
              class="chatbot-history-search"
            >
              <svg-element
                class="chatbot-history-search-icon"
                icon="search"
                size="14"
              />

              <input
                v-model="history_search_query"
                type="search"
                class="chatbot-history-search-input nds-text-p-small"
                :placeholder="$t('history_search_placeholder')"
                :aria-label="$t('history_search_placeholder')"
                autocomplete="off"
              >

              <button
                v-if="history_search_query"
                type="button"
                class="chatbot-history-search-clear"
                :title="$t('close')"
                :aria-label="$t('close')"
                @click="clearHistorySearch"
              >
                <svg-element
                  icon="close"
                  size="12"
                />
              </button>
            </div>

            <div
              v-if="is_loading_conversations"
              class="chatbot-loading"
            >
              <progress-circular-element
                indeterminate
                :size="24"
                width="2"
              />
            </div>

            <p
              v-else-if="!conversations.length"
              class="chatbot-history-empty nds-text-p-small"
            >
              {{ $t('no_conversations') }}
            </p>

            <p
              v-else-if="!filtered_conversations.length"
              class="chatbot-history-empty nds-text-p-small"
            >
              {{ $t('no_conversations_found') }}
            </p>

            <ul
              v-else
              class="chatbot-history-list"
            >
              <li
                v-for="conversation in filtered_conversations"
                :key="conversation.id"
                class="chatbot-history-row"
              >
                <button
                  type="button"
                  class="chatbot-history-item"
                  :class="{
                    'chatbot-history-item--active':
                      conversation.id === conversation_id,
                  }"
                  :disabled="is_streaming || is_loading"
                  @click="onSelectConversation(conversation.id)"
                >
                  <span class="chatbot-history-item-name nds-text-p-small">
                    {{ conversationName(conversation) }}
                  </span>
                  <span class="chatbot-history-item-date nds-text-tag">
                    {{ formatConversationDate(conversation) }}
                  </span>
                </button>

                <button
                  type="button"
                  class="chatbot-history-delete"
                  :title="$t('delete_conversation')"
                  :aria-label="$t('delete_conversation')"
                  :disabled="is_streaming || is_loading"
                  @click.stop="onDeleteConversation(conversation.id)"
                >
                  <svg-element
                    icon="trash"
                    size="14"
                  />
                </button>
              </li>
            </ul>
          </div>

          <div
            v-click-outside="onClickOutsideSettings"
            class="chatbot-main-area"
          >
            <div class="chatbot-main">
              <div
                ref="messages_container"
                class="chatbot-messages"
              >
                <div
                  v-if="is_loading && !messages.length"
                  class="chatbot-loading"
                >
                  <progress-circular-element
                    indeterminate
                    :size="28"
                    width="2"
                  />
                </div>

                <template v-else>
                  <div
                    v-if="show_welcome"
                    class="chatbot-message chatbot-message--assistant"
                  >
                    <img
                      class="chatbot-message-avatar chatbot-message-avatar--bot"
                      :src="bot_avatar_src"
                      alt=""
                    >

                    <div class="chatbot-message-content">
                      <!-- eslint-disable-next-line vue/no-v-html -->
                      <div
                        v-if="opening_statement && should_render_markdown"
                        class="chatbot-message-bubble nds-text-p chatbot-markdown"
                        v-html="renderMarkdown(opening_statement)"
                      />

                      <div
                        v-else-if="opening_statement"
                        class="chatbot-message-bubble nds-text-p chatbot-message-raw"
                      >
                        {{ opening_statement }}
                      </div>

                      <div
                        v-else
                        class="chatbot-message-bubble nds-text-p"
                      >
                        {{ $t('empty_state') }}
                      </div>

                      <div
                        v-if="suggested_questions.length"
                        class="chatbot-suggestions"
                      >
                        <button
                          v-for="(question, index) in suggested_questions"
                          :key="`suggestion-${index}`"
                          type="button"
                          class="chatbot-suggestion nds-text-p-small"
                          :class="{
                            'chatbot-suggestion--contextual': question.is_contextual,
                          }"
                          :disabled="is_streaming"
                          @click="onSuggestedQuestion(question.label)"
                        >
                          {{ question.label }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div
                    v-for="message in messages"
                    :key="message.id"
                    class="chatbot-message"
                    :class="`chatbot-message--${message.role}`"
                  >
                    <img
                      v-if="message.role === 'assistant'"
                      class="chatbot-message-avatar chatbot-message-avatar--bot"
                      :src="bot_avatar_src"
                      alt=""
                    >

                    <div
                      v-if="message.role === 'assistant'"
                      class="chatbot-message-content"
                    >
                      <div
                        class="chatbot-message-bubble nds-text-p"
                        :class="{ 'chatbot-markdown': should_render_markdown }"
                      >
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <div
                          v-if="String(message.content || '').trim() && should_render_markdown"
                          v-html="renderMarkdown(message.content)"
                        />

                        <div
                          v-else-if="String(message.content || '').trim()"
                          class="chatbot-message-raw"
                        >
                          {{ message.content }}
                        </div>

                        <span
                          v-if="message.status === 'streaming' && !String(message.content || '').trim()"
                          class="chatbot-typing"
                        >
                          <span
                            v-if="should_show_loading_steps && display_stream_status"
                            class="chatbot-typing-label"
                          >
                            {{ display_stream_status }}
                          </span>

                          <span
                            class="chatbot-typing-dots"
                            aria-hidden="true"
                          >
                            <span class="chatbot-typing-dot" />
                            <span class="chatbot-typing-dot" />
                            <span class="chatbot-typing-dot" />
                          </span>
                        </span>
                      </div>

                      <div
                        v-if="shouldShowFollowUpQuestions(message)"
                        class="chatbot-suggestions"
                      >
                        <button
                          v-for="(question, index) in message.suggested_questions"
                          :key="`follow-up-${message.id}-${index}`"
                          type="button"
                          class="chatbot-suggestion nds-text-p-small"
                          :disabled="is_streaming"
                          @click="onSuggestedQuestion(question)"
                        >
                          {{ question }}
                        </button>
                      </div>

                      <div
                        v-if="shouldShowCitations(message)"
                        class="chatbot-citations"
                      >
                        <div class="chatbot-citations-title nds-text-tag">
                          {{ $t('sources_title') }}
                        </div>

                        <div class="chatbot-citations-list">
                          <div
                            v-for="resource in message.retriever_resources"
                            :key="`${message.id}-${resource.id}-${resource.position}`"
                            class="chatbot-citation"
                          >
                            <button
                              type="button"
                              class="chatbot-citation-toggle"
                              :aria-expanded="isCitationExpanded(message.id, resource)"
                              @click="toggleCitation(message.id, resource)"
                            >
                              <span class="chatbot-citation-index nds-text-tag">
                                {{ resource.position }}
                              </span>
                              <span class="chatbot-citation-name nds-text-p-small">
                                {{ resource.document_name || $t('sources_untitled') }}
                              </span>
                              <span
                                v-if="resource.dataset_name"
                                class="chatbot-citation-dataset nds-text-tag"
                              >
                                {{ resource.dataset_name }}
                              </span>
                              <span
                                v-if="formatCitationScore(resource.score)"
                                class="chatbot-citation-score nds-text-tag"
                              >
                                {{ formatCitationScore(resource.score) }}
                              </span>
                            </button>

                            <div
                              v-if="isCitationExpanded(message.id, resource) && resource.content"
                              class="chatbot-citation-content nds-text-p-small"
                            >
                              {{ resource.content }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <template v-else>
                      <div class="chatbot-message-bubble nds-text-p">
                        {{ message.content }}
                      </div>

                      <img
                        class="chatbot-message-avatar"
                        :src="user_avatar_url"
                        alt=""
                        @error="onUserAvatarError"
                      >
                    </template>
                  </div>
                </template>
              </div>

              <footer class="chatbot-composer">
                <div class="chatbot-composer-field">
                  <textarea
                    ref="composer_input"
                    v-model="draft_message"
                    class="chatbot-composer-input nds-text-p"
                    :placeholder="$t('placeholder')"
                    :disabled="is_streaming"
                    rows="1"
                    @keydown="onComposerKeydown"
                  />

                  <div class="chatbot-composer-actions">
                    <button
                      type="button"
                      class="chatbot-composer-settings"
                      :class="{ 'chatbot-composer-settings--active': show_settings }"
                      :title="$t('settings_title')"
                      :aria-label="$t('settings_title')"
                      :aria-expanded="show_settings ? 'true' : 'false'"
                      @click="onToggleSettings"
                    >
                      <svg-element
                        icon="settings"
                        size="16"
                      />
                    </button>

                    <button
                      v-if="is_streaming"
                      type="button"
                      class="chatbot-composer-submit chatbot-composer-submit--stop"
                      :title="$t('stop')"
                      :aria-label="$t('stop')"
                      @click="onStop"
                    >
                      <svg-element
                        icon="stop"
                        size="16"
                      />
                    </button>

                    <button
                      v-else
                      type="button"
                      class="chatbot-composer-submit"
                      :title="$t('send')"
                      :aria-label="$t('send')"
                      :disabled="!can_send"
                      @click="onSend"
                    >
                      <svg-element
                        icon="send"
                        size="16"
                      />
                    </button>
                  </div>
                </div>
              </footer>
            </div>

            <div
              v-if="show_settings"
              class="chatbot-settings-menu"
              :class="{ 'chatbot-settings-menu--sidebar': is_expanded }"
            >
              <div class="chatbot-settings-menu-header">
                <div class="chatbot-settings-menu-title nds-text-p-bold">
                  <svg-element
                    icon="settings"
                    size="14"
                  />

                  <span>
                    {{ $t('settings_title') }}
                  </span>
                </div>

                <button
                  type="button"
                  class="chatbot-icon-button"
                  :title="$t('close')"
                  :aria-label="$t('close')"
                  @click="onCloseSettings"
                >
                  <svg-element
                    icon="close"
                    size="16"
                  />
                </button>
              </div>

              <section class="chatbot-settings-section">
                <div class="chatbot-settings-section-title nds-text-p-small-bold">
                  {{ $t('settings_font') }}
                </div>

                <label class="chatbot-settings-field">
                  <span class="chatbot-settings-field-label nds-text-p-small">
                    {{ $t('settings_font_family') }}
                  </span>
                  <select
                    class="chatbot-settings-select nds-text-p-small"
                    :value="chatbot_settings.font_family"
                    @change="onFontFamilyChange"
                  >
                    <option
                      v-for="option in font_family_options"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.name }}
                    </option>
                  </select>
                </label>

                <div class="chatbot-settings-field">
                  <span class="chatbot-settings-field-label nds-text-p-small">
                    {{ $t('settings_font_size') }}
                  </span>
                  <div
                    class="chatbot-settings-size-options"
                    role="group"
                    :aria-label="$t('settings_font_size')"
                  >
                    <button
                      v-for="option in font_size_options"
                      :key="option.value"
                      type="button"
                      class="chatbot-settings-size-option nds-text-p-small-bold"
                      :class="{
                        'chatbot-settings-size-option--active':
                          chatbot_settings.font_size === option.value,
                      }"
                      @click="onFontSizeChange(option.value)"
                    >
                      {{ option.name }}
                    </button>
                  </div>
                </div>
              </section>

              <section class="chatbot-settings-section">
                <toggle-element
                  :model-value="chatbot_settings.auto_scroll !== false"
                  :label="$t('settings_auto_scroll')"
                  @update:model-value="onAutoScrollChange"
                />
              </section>

              <section
                v-if="is_chatbot_admin"
                class="chatbot-settings-section"
              >
                <div class="chatbot-settings-section-title nds-text-p-small-bold">
                  {{ $t('settings_admin') }}
                </div>

                <toggle-element
                  :model-value="chatbot_settings.render_markdown"
                  :label="$t('settings_render_markdown')"
                  @update:model-value="onRenderMarkdownChange"
                />

                <toggle-element
                  :model-value="chatbot_settings.show_detailed_loading_steps !== false"
                  :label="$t('settings_detailed_loading_steps')"
                  @update:model-value="onDetailedLoadingStepsChange"
                />

                <toggle-element
                  :model-value="!chatbot_settings.raw_workflow_steps"
                  :label="$t('settings_formatted_workflow_steps')"
                  :disabled="chatbot_settings.show_detailed_loading_steps === false"
                  @update:model-value="onFormattedWorkflowStepsChange"
                />

                <toggle-element
                  :model-value="Boolean(chatbot_settings.show_follow_up_suggestions)"
                  :label="$t('settings_follow_up_suggestions')"
                  @update:model-value="onFollowUpSuggestionsChange"
                />

                <toggle-element
                  :model-value="Boolean(chatbot_settings.show_sources)"
                  :label="$t('settings_sources')"
                  @update:model-value="onSourcesChange"
                />

                <label class="chatbot-settings-field">
                  <span class="chatbot-settings-field-label nds-text-p-small">
                    {{ $t('settings_api_key') }}

                    <svg-element
                      icon="key"
                      size="12"
                    />
                  </span>
                  <div class="chatbot-settings-api-key">
                    <div class="chatbot-settings-api-key-field">
                      <input
                        v-model="draft_api_key"
                        class="chatbot-settings-input nds-text-p-small"
                        :type="show_api_key ? 'text' : 'password'"
                        autocomplete="off"
                        spellcheck="false"
                        :placeholder="$t('settings_api_key_placeholder')"
                        @keydown.enter.prevent="onSaveApiKey"
                      >
                      <button
                        type="button"
                        class="chatbot-settings-api-key-reveal"
                        :class="{
                          'chatbot-settings-api-key-reveal--visible': show_api_key,
                        }"
                        :title="show_api_key
                          ? $t('settings_api_key_hide')
                          : $t('settings_api_key_show')"
                        :aria-label="show_api_key
                          ? $t('settings_api_key_hide')
                          : $t('settings_api_key_show')"
                        @click="onToggleApiKeyVisibility"
                      >
                        <svg-element
                          :icon="show_api_key ? 'slashed-eye' : 'eye'"
                          size="14"
                        />
                      </button>
                    </div>

                    <button
                      type="button"
                      class="chatbot-settings-api-key-save nds-text-p-small-bold"
                      :disabled="!can_save_api_key"
                      @click="onSaveApiKey"
                    >
                      {{ $t('settings_api_key_save') }}
                    </button>
                  </div>
                </label>
              </section>
            </div>
          </div>
        </div>
      </div>

      <button
        v-if="!is_expanded"
        type="button"
        class="chatbot-bubble"
        :class="{ 'chatbot-bubble--dragging': is_dragging }"
        :title="is_open ? $t('close_chatbot') : $t('open_chatbot')"
        :aria-label="is_open ? $t('close_chatbot') : $t('open_chatbot')"
        @pointerdown="onBubblePointerDown"
        @click="onBubbleClick"
      >
        <img
          v-if="!is_open"
          class="chatbot-bubble-logo"
          :src="nomination_logo_src"
          alt=""
        >

        <svg-element
          v-else
          icon="close"
          size="24"
        />
      </button>
    </div>
  </Teleport>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import configuration from '@/configurations';
import ProgressCircularElement from '@/components/elements/ProgressCircularElement.vue';
import SvgElement from '@/components/elements/SvgElement.vue';
import ToggleElement from '@/components/elements/ToggleElement.vue';
import {
  applySuggestionTemplate,
  getAccountIdFromRoute,
  getCurrentPageName,
  parseSuggestedQuestion,
  parseWorkflowNodeStatusLabel,
  pickOpeningStatement,
  suggestionMatchesRoute,
} from '@/utils/chatbotSuggestions';
import {
  CHATBOT_FONT_FAMILY_OPTIONS,
  CHATBOT_FONT_SIZE_OPTIONS,
  getChatbotFontFamilyCss,
} from '@/utils/chatbotSettings';
import { renderMarkdown } from '@/utils/markdown';

const EXTERNAL_LINK_ICON_URL = require('@/assets/icons/nds/external-link.svg');

const DRAG_CLICK_THRESHOLD_PX = 6;
const CHATBOT_EDGE_MARGIN_PX = 24;
const CHATBOT_BUBBLE_SIZE_PX = 56;
const CHATBOT_PANEL_WIDTH_PX = 480;
const MAN_CIVILITY_ID = 1;
const DEFAULT_MALE_AVATAR_PATH = '/images/dummy/avatar.png';
const DEFAULT_FEMALE_AVATAR_PATH = '/images/dummy/avatarF.png';

const isHttpAvatarUrl = (url) => typeof url === 'string'
  && !url.startsWith('data:')
  && (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/'));

export default {
  name: 'ChatbotElement',
  components: {
    ProgressCircularElement,
    SvgElement,
    ToggleElement,
  },
  data() {
    return {
      draft_message: '',
      history_search_query: '',
      is_dragging: false,
      drag_start_client_x: 0,
      drag_start_offset_x: 0,
      suppress_bubble_click: false,
      user_avatar_failed: false,
      show_settings: false,
      is_expanded: false,
      is_mobile_viewport: false,
      expanded_citation_key: null,
      font_family_options: CHATBOT_FONT_FAMILY_OPTIONS,
      font_size_options: CHATBOT_FONT_SIZE_OPTIONS,
      nomination_logo_src: require('@/assets/images/nomination-logo-white-light.png'),
      bot_avatar_src: require('@/assets/images/nomination-logo-white-light.png'),
      draft_api_key: '',
      show_api_key: false,
      is_saving_api_key: false,
    };
  },
  computed: {
    ...mapGetters({
      is_connected: 'user/isConnected',
      user_token: 'user/token',
      user_data: 'user/userData',
      user_is_alpha: 'user/userIsAlpha',
      user_property_list: 'user/userPropertyList',
      user_actual_experience: 'user/userActualExperience',
      viewed_contact: 'contact/contact',
      viewed_account: 'account/account',
      is_open: 'chatbot/isOpen',
      show_conversations: 'chatbot/showConversations',
      messages: 'chatbot/messages',
      conversation_id: 'chatbot/conversationId',
      conversations: 'chatbot/conversations',
      app_info: 'chatbot/appInfo',
      parameters: 'chatbot/parameters',
      is_loading: 'chatbot/isLoading',
      is_loading_conversations: 'chatbot/isLoadingConversations',
      is_streaming: 'chatbot/isStreaming',
      stream_status: 'chatbot/streamStatus',
      show_workflow_steps: 'chatbot/showWorkflowSteps',
      has_messages: 'chatbot/hasMessages',
      chatbot_settings: 'chatbot/settings',
      drag_offset_x: 'chatbot/dragOffsetX',
    }),
    should_show_chatbot() {
      return this.is_connected && this.user_is_alpha;
    },
    is_chatbot_admin() {
      const admin_level_id = Number(this.user_data?.admin_level?.id);

      return Number.isFinite(admin_level_id) && admin_level_id > 0;
    },
    should_render_markdown() {
      if (!this.is_chatbot_admin) {
        return true;
      }

      return this.chatbot_settings?.render_markdown !== false;
    },
    should_show_loading_steps() {
      if (this.is_chatbot_admin) {
        return this.chatbot_settings?.show_detailed_loading_steps !== false;
      }

      return this.show_workflow_steps;
    },
    display_stream_status() {
      if (!this.stream_status) {
        return '';
      }

      if (this.is_chatbot_admin && this.chatbot_settings?.raw_workflow_steps) {
        return this.stream_status;
      }

      return parseWorkflowNodeStatusLabel(this.stream_status);
    },
    chatbot_content_style() {
      return {
        '--chatbot-font-family': getChatbotFontFamilyCss(
          this.chatbot_settings?.font_family
        ),
        '--chatbot-font-size': `${this.chatbot_settings?.font_size || 15}px`,
        '--chatbot-line-height': `${Math.round((this.chatbot_settings?.font_size || 15) * 1.35)}px`,
        '--chatbot-external-link-icon': `url("${EXTERNAL_LINK_ICON_URL}")`,
      };
    },
    show_history_panel() {
      if (this.show_conversations) {
        return true;
      }

      return this.is_expanded && !this.is_mobile_viewport;
    },
    show_history_toggle() {
      return !this.is_expanded || this.is_mobile_viewport;
    },
    filtered_conversations() {
      const query = this.history_search_query.trim().toLowerCase();

      if (!query) {
        return this.conversations;
      }

      return this.conversations.filter((conversation) => {
        const name = this.conversationName(conversation).toLowerCase();

        return name.includes(query);
      });
    },
    chatbot_title() {
      return this.app_info?.name || this.$t('title');
    },
    chatbot_position_style() {
      return {
        transform: `translateX(${this.drag_offset_x}px)`,
      };
    },
    stable_user_id() {
      if (!this.user_data) {
        return undefined;
      }

      return `${this.user_data.id}-${this.user_data.first_name}-${this.user_data.last_name}`
        .toLowerCase();
    },
    opening_statement() {
      return pickOpeningStatement(
        this.parameters?.opening_statement,
        this.$route,
        this.suggestion_template_variables
      );
    },
    current_page() {
      return getCurrentPageName(this.$route);
    },
    account_id() {
      return getAccountIdFromRoute(this.$route);
    },
    suggested_questions() {
      return (this.parameters?.suggested_questions || [])
        .filter((question) => typeof question === 'string' && question.trim())
        .map((question) => parseSuggestedQuestion(question))
        .filter((parsed) => parsed.label && suggestionMatchesRoute(parsed, this.$route))
        .map((parsed) => {
          const templated = applySuggestionTemplate(
            parsed.label,
            this.suggestion_template_variables
          );

          return {
            label: templated.label,
            is_complete: templated.is_complete,
            is_contextual: !parsed.is_wildcard,
          };
        })
        .filter((question) => question.is_complete && question.label);
    },
    show_welcome() {
      return !this.has_messages;
    },
    can_send() {
      return Boolean(this.draft_message.trim())
        && !this.is_streaming
        && Boolean(this.stable_user_id);
    },
    chatbot_environment() {
      return configuration.chatbot_environment;
    },
    desk_v1_host() {
      return configuration.desk_v1_host || '';
    },
    value_proposition() {
      return this.user_property_list?.find(
        (property) => property.name === 'value_proposition'
      )?.value || '';
    },
    current_job() {
      return this.user_actual_experience?.job_title || '';
    },
    current_company() {
      return this.user_actual_experience?.company?.name || '';
    },
    organization_id() {
      return this.user_data?.organization?.id ?? 0;
    },
    contact_in_context() {
      // Page stores keep the last visited record, so ignore it once we leave its page.
      const contact_id = Number(this.$route?.params?.contact_id);

      if (!Number.isFinite(contact_id) || Number(this.viewed_contact?.id) !== contact_id) {
        return null;
      }

      return this.viewed_contact;
    },
    account_in_context() {
      if (!this.account_id || Number(this.viewed_account?.id) !== this.account_id) {
        return null;
      }

      return this.viewed_account;
    },
    // add new template variables for the chatbot suggestions here
    suggestion_template_variables() {
      return {
        contact_first_name: this.contact_in_context?.first_name || '',
        contact_last_name: this.contact_in_context?.last_name || '',
        account_name: this.account_in_context?.name || '',
      };
    },
    default_avatar_url() {
      const civility_id = this.user_data?.civility?.id;
      const avatar_path = (civility_id == MAN_CIVILITY_ID || civility_id === undefined)
        ? DEFAULT_MALE_AVATAR_PATH
        : DEFAULT_FEMALE_AVATAR_PATH;

      return avatar_path;
    },
    user_avatar_url() {
      if (this.user_avatar_failed) {
        return this.default_avatar_url;
      }

      const user_picture = this.user_data?.picture_url || this.user_data?.avatar_url;

      if (isHttpAvatarUrl(user_picture)) {
        return user_picture;
      }

      return this.default_avatar_url;
    },
    saved_api_key() {
      return this.chatbot_settings?.api_key || '';
    },
    can_save_api_key() {
      if (this.is_saving_api_key) {
        return false;
      }

      return this.draft_api_key.trim() !== this.saved_api_key;
    },
  },
  watch: {
    'user_data.id'() {
      this.user_avatar_failed = false;
    },
    should_show_chatbot: {
      immediate: true,
      handler(should_show) {
        if (!should_show) {
          this.resetOnLogout();
          this.resetDragPosition();
          this.show_settings = false;
          this.is_expanded = false;
          this.clearHistorySearch();
          this.unlockBodyScroll();
        }
      },
    },
    is_open(is_open) {
      if (is_open && this.should_show_chatbot) {
        this.bootstrapPanel();
        this.focusComposerInput();
      }

      if (!is_open) {
        this.show_settings = false;
        this.is_expanded = false;
        this.clearHistorySearch();
        this.unlockBodyScroll();
      }

      this.syncClampedDragOffset({ persist: true });
    },
    show_settings(show_settings) {
      if (show_settings) {
        this.syncDraftApiKey();
      } else {
        this.show_api_key = false;
      }
    },
    is_expanded(is_expanded) {
      if (is_expanded) {
        this.lockBodyScroll();
        this.$nextTick(() => {
          this.scrollMessagesToBottom();
          this.focusComposerInput();
        });
      } else {
        this.unlockBodyScroll();
      }
    },
    messages: {
      deep: true,
      handler() {
        if (this.chatbot_settings?.auto_scroll === false) {
          return;
        }

        this.$nextTick(() => {
          this.scrollMessagesToBottom();
        });
      },
    },
  },
  mounted() {
    this.is_mobile_viewport = window.innerWidth <= 640;
    this.syncClampedDragOffset({ persist: true });
    window.addEventListener('resize', this.onWindowResize);
    window.addEventListener('keydown', this.onWindowKeydown);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('keydown', this.onWindowKeydown);
    this.removeDragListeners();
    this.unlockBodyScroll();

    if (!this.should_show_chatbot) {
      this.resetOnLogout();
    }
  },
  methods: {
    ...mapActions({
      triggerToast: 'toast_v2/triggerToast',
      getUserPropertyList: 'user/getUserPropertyList',
      getUserExperienceList: 'user/getUserExperienceList',
      toggleChatbot: 'chatbot/toggleChatbot',
      openConversationsMenu: 'chatbot/openConversationsMenu',
      closeConversationsMenu: 'chatbot/closeConversationsMenu',
      fetchAppInfo: 'chatbot/fetchAppInfo',
      fetchSiteSettings: 'chatbot/fetchSiteSettings',
      fetchParameters: 'chatbot/fetchParameters',
      fetchConversations: 'chatbot/fetchConversations',
      loadConversation: 'chatbot/loadConversation',
      deleteConversation: 'chatbot/deleteConversation',
      startNewConversation: 'chatbot/startNewConversation',
      sendMessage: 'chatbot/sendMessage',
      stopGeneration: 'chatbot/stopGeneration',
      resetOnLogout: 'chatbot/resetOnLogout',
      updateSettings: 'chatbot/updateSettings',
      applyApiKeyChange: 'chatbot/applyApiKeyChange',
      updateDragOffset: 'chatbot/updateDragOffset',
    }),
    showErrorToast(translation_key) {
      this.triggerToast({
        style: 'error',
        text: this.$t(translation_key),
        timeout: 5000,
      });
    },
    onUserAvatarError() {
      this.user_avatar_failed = true;
    },
    renderMarkdown,
    onToggleSettings() {
      this.show_settings = !this.show_settings;
    },
    onCloseSettings() {
      this.show_settings = false;
    },
    onClickOutsideSettings() {
      if (this.is_expanded) {
        return;
      }

      this.onCloseSettings();
    },
    lockBodyScroll() {
      document.body.style.overflow = 'hidden';
    },
    unlockBodyScroll() {
      document.body.style.overflow = '';
    },
    onWindowKeydown(event) {
      if (event.key === 'Escape' && this.is_expanded) {
        this.onCollapse();
      }
    },
    async onToggleExpand() {
      if (this.is_expanded) {
        this.onCollapse();

        return;
      }

      await this.onExpand();
    },
    async onExpand() {
      if (!this.is_open) {
        await this.toggleChatbot();
      }

      this.show_settings = false;
      this.closeConversationsMenu();

      if (this.stable_user_id) {
        try {
          await this.fetchConversations({
            user: this.stable_user_id,
            environment: this.chatbot_environment,
          });
        } catch {
          this.showErrorToast('error_load_conversations');
        }
      }

      this.is_expanded = true;
    },
    onCollapse() {
      this.is_expanded = false;
      this.show_settings = false;
      this.focusComposerInput();
    },
    onFontFamilyChange(event) {
      this.updateSettings({ font_family: event.target.value });
    },
    onFontSizeChange(font_size) {
      this.updateSettings({ font_size });
    },
    onAutoScrollChange(auto_scroll) {
      this.updateSettings({ auto_scroll });
    },
    onRenderMarkdownChange(render_markdown) {
      this.updateSettings({ render_markdown });
    },
    onDetailedLoadingStepsChange(show_detailed_loading_steps) {
      this.updateSettings({ show_detailed_loading_steps });
    },
    onFormattedWorkflowStepsChange(formatted) {
      if (this.chatbot_settings?.show_detailed_loading_steps === false) {
        return;
      }

      this.updateSettings({ raw_workflow_steps: !formatted });
    },
    onFollowUpSuggestionsChange(show_follow_up_suggestions) {
      this.updateSettings({ show_follow_up_suggestions });
    },
    onSourcesChange(show_sources) {
      this.updateSettings({ show_sources });
    },
    syncDraftApiKey() {
      this.draft_api_key = this.saved_api_key;
      this.show_api_key = false;
    },
    onToggleApiKeyVisibility() {
      this.show_api_key = !this.show_api_key;
    },
    async onSaveApiKey() {
      if (!this.can_save_api_key) {
        return;
      }

      this.is_saving_api_key = true;

      try {
        await this.applyApiKeyChange({ api_key: this.draft_api_key });
        this.syncDraftApiKey();
        await this.bootstrapPanel();
      } finally {
        this.is_saving_api_key = false;
      }
    },
    getWidgetWidth() {
      if (!this.is_open) {
        return CHATBOT_BUBBLE_SIZE_PX;
      }

      return Math.min(CHATBOT_PANEL_WIDTH_PX, window.innerWidth - (CHATBOT_EDGE_MARGIN_PX * 2));
    },
    clampDragOffset(offset) {
      const min_offset = -(
        window.innerWidth - this.getWidgetWidth() - (CHATBOT_EDGE_MARGIN_PX * 2)
      );

      return Math.min(0, Math.max(min_offset, offset));
    },
    syncClampedDragOffset({ persist = false } = {}) {
      const next_offset = this.clampDragOffset(this.drag_offset_x);

      if (next_offset === this.drag_offset_x) {
        return next_offset;
      }

      this.updateDragOffset({
        offset: next_offset,
        persist,
      });

      return next_offset;
    },
    onWindowResize() {
      this.is_mobile_viewport = window.innerWidth <= 640;
      this.syncClampedDragOffset({ persist: true });
    },
    resetDragPosition() {
      this.is_dragging = false;
      this.suppress_bubble_click = false;
      this.removeDragListeners();
    },
    removeDragListeners() {
      window.removeEventListener('pointermove', this.onBubblePointerMove);
      window.removeEventListener('pointerup', this.onBubblePointerUp);
      window.removeEventListener('pointercancel', this.onBubblePointerUp);
    },
    onBubblePointerDown(event) {
      if (event.button !== undefined && event.button !== 0) {
        return;
      }

      this.is_dragging = true;
      this.suppress_bubble_click = false;
      this.drag_start_client_x = event.clientX;
      this.drag_start_offset_x = this.drag_offset_x;

      window.addEventListener('pointermove', this.onBubblePointerMove);
      window.addEventListener('pointerup', this.onBubblePointerUp);
      window.addEventListener('pointercancel', this.onBubblePointerUp);
    },
    onBubblePointerMove(event) {
      if (!this.is_dragging) {
        return;
      }

      const delta_x = event.clientX - this.drag_start_client_x;

      if (Math.abs(delta_x) > DRAG_CLICK_THRESHOLD_PX) {
        this.suppress_bubble_click = true;
      }

      this.updateDragOffset({
        offset: this.clampDragOffset(this.drag_start_offset_x + delta_x),
        persist: false,
      });
    },
    onBubblePointerUp() {
      this.is_dragging = false;
      this.removeDragListeners();
      this.updateDragOffset({
        offset: this.drag_offset_x,
        persist: true,
      });
    },
    onBubbleClick() {
      if (this.suppress_bubble_click) {
        this.suppress_bubble_click = false;

        return;
      }

      this.toggleChatbot();
    },
    conversationName(conversation) {
      const name = (conversation?.name || '').trim();

      if (name && name.toLowerCase() !== 'new chat') {
        return name;
      }

      return this.$t('untitled_conversation');
    },
    formatConversationDate(conversation) {
      const timestamp = conversation?.updated_at || conversation?.created_at;

      if (!timestamp) {
        return '';
      }

      const date = new Date(
        typeof timestamp === 'number' ? timestamp * 1000 : timestamp
      );

      if (Number.isNaN(date.getTime())) {
        return '';
      }

      return date.toLocaleString(this.$i18n.locale, {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    async bootstrapPanel() {
      if (!this.stable_user_id) {
        return;
      }

      try {
        await this.fetchAppInfo();
      } catch {
        // Keep i18n fallback title if /info is unavailable
      }

      try {
        await this.fetchSiteSettings();
      } catch {
        // Default to hidden workflow steps if /site is unavailable
      }

      try {
        await this.fetchParameters({ user: this.stable_user_id });
      } catch {
        this.showErrorToast('error_load_parameters');
      }

      try {
        await this.getUserPropertyList({
          name_list: ['value_proposition'],
        });
      } catch {
        // Keep empty value_proposition if user properties are unavailable
      }

      try {
        await this.getUserExperienceList();
      } catch {
        // Keep empty current_job / current_company if experience is unavailable
      }

      try {
        await this.fetchConversations({
          user: this.stable_user_id,
          environment: this.chatbot_environment,
        });
      } catch {
        this.showErrorToast('error_load_conversations');
      }

      this.$nextTick(() => {
        this.scrollMessagesToBottom();
      });
    },
    scrollMessagesToBottom() {
      const container = this.$refs.messages_container;

      if (!container) {
        return;
      }

      container.scrollTop = container.scrollHeight;
    },
    focusComposerInput() {
      this.$nextTick(() => {
        const composer_input = this.$refs.composer_input;

        if (!composer_input || composer_input.disabled) {
          return;
        }

        composer_input.focus();
      });
    },
    clearHistorySearch() {
      this.history_search_query = '';
    },
    onCloseConversations() {
      this.clearHistorySearch();
      this.closeConversationsMenu();
      this.focusComposerInput();
    },
    async onToggleConversations() {
      if (this.show_conversations) {
        this.clearHistorySearch();
        this.closeConversationsMenu();
        this.focusComposerInput();

        return;
      }

      if (!this.stable_user_id) {
        return;
      }

      this.show_settings = false;

      try {
        await this.openConversationsMenu({
          user: this.stable_user_id,
          environment: this.chatbot_environment,
        });
      } catch {
        this.showErrorToast('error_load_conversations');
      }
    },
    async onNewConversation() {
      this.expanded_citation_key = null;
      await this.startNewConversation();
      this.focusComposerInput();
    },
    async onSelectConversation(conversation_id) {
      if (!this.stable_user_id || conversation_id === this.conversation_id) {
        this.closeConversationsMenu();
        this.focusComposerInput();

        return;
      }

      this.expanded_citation_key = null;
      try {
        await this.loadConversation({
          conversation_id,
          user: this.stable_user_id,
        });
        this.focusComposerInput();
      } catch {
        this.showErrorToast('error_load_history');
      }
    },
    async onDeleteConversation(conversation_id) {
      if (!this.stable_user_id || !conversation_id) {
        return;
      }

      try {
        await this.deleteConversation({
          conversation_id,
          user: this.stable_user_id,
        });
      } catch {
        this.showErrorToast('error_delete_conversation');
      }
    },
    onComposerKeydown(event) {
      if (event.key !== 'Enter' || event.shiftKey) {
        return;
      }

      event.preventDefault();
      this.onSend();
    },
    async onSuggestedQuestion(question) {
      this.draft_message = question;
      await this.onSend();
    },
    shouldShowFollowUpQuestions(message) {
      if (!this.chatbot_settings?.show_follow_up_suggestions) {
        return false;
      }

      if (
        this.is_streaming
        || message?.role !== 'assistant'
        || message?.status !== 'done'
        || !message?.suggested_questions?.length
      ) {
        return false;
      }

      const last_assistant = [...this.messages]
        .reverse()
        .find((item) => item.role === 'assistant');

      return last_assistant?.id === message.id;
    },
    shouldShowCitations(message) {
      if (!this.chatbot_settings?.show_sources) {
        return false;
      }

      if (message?.role !== 'assistant' || message?.status !== 'done') {
        return false;
      }

      return Boolean(message?.retriever_resources?.length);
    },
    getCitationKey(message_id, resource) {
      return `${message_id}:${resource.id}:${resource.position}`;
    },
    isCitationExpanded(message_id, resource) {
      return this.expanded_citation_key === this.getCitationKey(message_id, resource);
    },
    toggleCitation(message_id, resource) {
      const key = this.getCitationKey(message_id, resource);

      this.expanded_citation_key = this.expanded_citation_key === key
        ? null
        : key;
    },
    formatCitationScore(score) {
      if (!Number.isFinite(score)) {
        return '';
      }

      return `${Math.round(score * 100)}%`;
    },
    async onSend() {
      if (!this.can_send) {
        return;
      }

      const query = this.draft_message.trim();

      this.draft_message = '';
      this.closeConversationsMenu();
      this.show_settings = false;

      try {
        await this.sendMessage({
          query,
          user: this.stable_user_id,
          inputs: {
            user_token: this.user_token,
            current_page: this.current_page,
            account_id: this.account_id,
            environment: this.chatbot_environment,
            value_proposition: this.value_proposition,
            current_job: this.current_job,
            current_company: this.current_company,
            organization_id: this.organization_id,
            desk_v1_host: this.desk_v1_host,
          },
        });
      } catch {
        this.showErrorToast('error_send');
      }
    },
    async onStop() {
      if (!this.stable_user_id) {
        return;
      }

      await this.stopGeneration({ user: this.stable_user_id });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/constants/variables.module.scss';

.chatbot-element {
  bottom: 24px;
  position: fixed;
  right: 24px;
  z-index: 1200;
  will-change: transform;

  &--expanded {
    bottom: 0;
    left: 0;
    right: 0;
    top: 0;
    will-change: auto;
    z-index: map-get($zindex, popup);
  }
}

.chatbot-expanded-backdrop {
  background-color: rgba($haiti, 0.45);
  inset: 0;
  position: absolute;
}

.chatbot-bubble {
  align-items: center;
  background-color: $cornflower-blue;
  border: none;
  border-radius: 50%;
  box-shadow: 0 8px 24px rgba($haiti, 0.18);
  color: $white;
  cursor: grab;
  display: flex;
  height: 56px;
  justify-content: center;
  touch-action: none;
  user-select: none;
  width: 56px;

  :deep(svg) {
    --color: #{$white};
  }

  &:hover {
    background-color: $han-blue;
  }

  &--dragging {
    cursor: grabbing;
  }
}

.chatbot-bubble-logo {
  display: block;
  height: 28px;
  object-fit: contain;
  pointer-events: none;
  width: 28px;
}

.chatbot-panel {
  background-color: $white;
  border: 1px solid $greyscale-semi-light;
  border-radius: 16px;
  bottom: 72px;
  box-shadow: 0 2px 4px rgba($haiti, 0.12), 0 8px 16px rgba($haiti, 0.18);
  display: flex;
  flex-direction: column;
  font-family: var(--chatbot-font-family, 'DM Sans', sans-serif);
  font-size: var(--chatbot-font-size, 15px);
  height: 45rem;
  line-height: var(--chatbot-line-height, 20px);
  max-height: calc(100vh - 120px - #{$app-header-height});
  overflow: hidden;
  position: absolute;
  right: 0;
  width: 30rem;

  .nds-text-p,
  .nds-text-p-bold,
  .nds-text-p-small,
  .nds-text-p-small-bold,
  .nds-text-button-small-bold,
  .nds-text-tag {
    font-family: inherit;
    font-size: inherit;
    letter-spacing: normal;
    line-height: inherit;
  }

  &--expanded {
    bottom: auto;
    height: min(92vh, 920px);
    left: 50%;
    max-height: none;
    max-width: 1280px;
    right: auto;
    top: 50%;
    transform: translate(-50%, -50%);
    width: min(92vw, 1280px);
    z-index: 1;
  }
}

.chatbot-panel-header {
  align-items: center;
  border-bottom: 1px solid $greyscale-semi-light;
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;
}

.chatbot-panel-header-left {
  align-items: center;
  display: flex;
  gap: 4px;
  min-width: 0;
}

.chatbot-panel-header-title.nds-text-p-bold {
  color: $haiti;
  font-size: 16px;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chatbot-panel-header-actions {
  align-items: center;
  display: flex;
  gap: 4px;
}

.chatbot-header-action {
  align-items: center;
  background: transparent;
  border: 1px solid $greyscale-semi-light;
  border-radius: 8px;
  color: $haiti;
  cursor: pointer;
  display: flex;
  gap: 6px;
  height: 32px;
  padding: 0 10px;

  :deep(svg) {
    --color: #{$haiti};
  }

  &:hover {
    border-color: $cornflower-blue;
    color: $cornflower-blue;

    :deep(svg) {
      --color: #{$cornflower-blue};
    }
  }

  &--active {
    background-color: $low-grey;
    border-color: $cornflower-blue;
    color: $cornflower-blue;

    :deep(svg) {
      --color: #{$cornflower-blue};
    }
  }
}

.chatbot-icon-button {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: $haiti;
  cursor: pointer;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;

  :deep(svg) {
    --color: #{$haiti};
  }

  &:hover:not(:disabled) {
    background-color: $low-grey;

    :deep(svg) {
      --color: #{$cornflower-blue};
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.chatbot-panel-body {
  display: flex;
  flex: 1;
  min-height: 0;
  position: relative;
}

.chatbot-history-menu {
  background-color: $white;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  left: 0;
  padding: 12px;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 2;

  &--sidebar {
    border-right: 1px solid $greyscale-semi-light;
    bottom: auto;
    flex-shrink: 0;
    left: auto;
    position: relative;
    right: auto;
    top: auto;
    width: 280px;
    z-index: 1;
  }
}

.chatbot-history-menu-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 0 4px 4px;
}

.chatbot-history-search {
  align-items: center;
  background-color: $white;
  border: 1px solid $greyscale-semi-light;
  border-radius: 8px;
  display: flex;
  gap: 8px;
  padding: 0 8px;
  position: relative;

  &:focus-within {
    border-color: $cornflower-blue;
  }

  :deep(svg) {
    --color: #{$greyscale-medium};
  }
}

.chatbot-history-search-icon {
  display: flex;
  flex-shrink: 0;
}

#app input.chatbot-history-search-input,
.chatbot-element input.chatbot-history-search-input {
  background: transparent;
  border: none;
  color: $haiti;
  flex: 1;
  font-family: inherit;
  font-size: inherit;
  letter-spacing: normal;
  line-height: inherit;
  min-width: 0;
  outline: none;
  padding: 8px 0;
  width: 100%;

  &::placeholder {
    color: $greyscale-medium;
  }

  &::-webkit-search-cancel-button {
    appearance: none;
    display: none;
  }
}

.chatbot-history-search-clear {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 22px;
  justify-content: center;
  padding: 0;
  width: 22px;

  :deep(svg) {
    --color: #{$greyscale-medium};
  }

  &:hover {
    background-color: $low-grey;

    :deep(svg) {
      --color: #{$haiti};
    }
  }
}

.chatbot-history-empty {
  color: $greyscale-medium;
  margin: 12px 4px 0;
}

.chatbot-history-list {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  list-style: none;
  margin: 0;
  overflow-y: auto;
  padding: 0 12px 0 0;
  scrollbar-gutter: stable;
}

.chatbot-history-row {
  position: relative;

  &:hover .chatbot-history-delete:not(:disabled),
  &:focus-within .chatbot-history-delete:not(:disabled) {
    opacity: 1;
  }

  &:hover .chatbot-history-item:not(:disabled),
  &:focus-within .chatbot-history-item:not(:disabled) {
    background-color: $low-grey;
  }
}

.chatbot-history-item {
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 10px 12px;
  text-align: left;
  width: 100%;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &--active {
    background-color: $low-grey;
    box-shadow: inset 0 0 0 1px $cornflower-blue;
  }
}

.chatbot-history-delete {
  align-items: center;
  background-color: $low-grey;
  border: none;
  border-radius: 6px;
  color: $haiti;
  cursor: pointer;
  display: flex;
  height: 28px;
  justify-content: center;
  opacity: 0;
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  transition: opacity 0.12s ease;
  width: 28px;
  z-index: 1;

  :deep(svg) {
    --color: #{$greyscale-medium};
  }

  &:hover:not(:disabled) {
    :deep(svg) {
      --color: #{$status-error};
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0;
  }
}

.chatbot-history-item-name {
  color: $haiti;
  display: -webkit-box;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

.chatbot-history-item-date {
  color: $greyscale-medium;
}

.chatbot-main-area {
  display: flex;
  flex: 1;
  min-height: 0;
  min-width: 0;
  position: relative;
}

.chatbot-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.chatbot-panel--expanded .chatbot-messages {
  margin: 0 auto;
  max-width: 760px;
  padding: 24px 32px;
  width: 100%;
}

.chatbot-panel--expanded .chatbot-composer {
  margin: 0 auto;
  max-width: 760px;
  padding: 12px 32px 20px;
  width: 100%;
}

.chatbot-messages {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  padding: 16px;
}

.chatbot-loading {
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
  padding: 24px;
}

.chatbot-message-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  max-width: calc(100% - 40px);
  min-width: 0;

  .chatbot-message-bubble {
    max-width: 100%;
  }
}

.chatbot-suggestions {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.chatbot-citations {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
  max-width: 100%;
}

.chatbot-citations-title {
  color: $greyscale-medium;
  text-transform: uppercase;
}

.chatbot-citations-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.chatbot-citation {
  max-width: 100%;
}

.chatbot-citation-toggle {
  align-items: center;
  background: transparent;
  border: 0;
  color: $haiti;
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 100%;
  padding: 0;
  text-align: left;

  &:hover .chatbot-citation-name {
    color: $cornflower-blue;
  }
}

.chatbot-citation-index {
  align-items: center;
  background-color: $low-grey;
  border: 1px solid $greyscale-semi-light;
  border-radius: 4px;
  color: $greyscale-medium;
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
  min-width: 18px;
  padding: 0 4px;
}

.chatbot-citation-name {
  color: $haiti;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chatbot-citation-dataset,
.chatbot-citation-score {
  color: $greyscale-medium;
}

.chatbot-citation-content {
  border-left: 2px solid $greyscale-semi-light;
  color: $greyscale-dark;
  margin: 4px 0 4px 8px;
  max-height: 160px;
  overflow-y: auto;
  padding-left: 8px;
  white-space: pre-wrap;
  word-break: break-word;
}

.chatbot-suggestion {
  background-color: $low-grey;
  border: 1px solid $greyscale-semi-light;
  border-radius: 8px;
  color: $haiti;
  cursor: pointer;
  max-width: 100%;
  padding: 4px 8px;
  text-align: left;
  width: fit-content;

  &:hover:not(:disabled) {
    border-color: $cornflower-blue;
    color: $cornflower-blue;
  }

  &--contextual {
    border-color: $cornflower-blue;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.chatbot-message {
  align-items: flex-start;
  display: flex;
  gap: 8px;
  width: 100%;

  &--user {
    justify-content: flex-end;

    .chatbot-message-bubble {
      background-color: $cornflower-blue;
      color: $white;
    }
  }

  &--assistant {
    justify-content: flex-start;

    .chatbot-message-bubble {
      background-color: $low-grey;
      color: $haiti;
    }
  }
}

.chatbot-message-avatar {
  border-radius: 50%;
  flex-shrink: 0;
  height: 32px;
  object-fit: cover;
  width: 32px;

  &--bot {
    background-color: $cornflower-blue;
    object-fit: contain;
    padding: 7px;
  }
}

#app .chatbot-message-bubble,
.chatbot-element .chatbot-message-bubble {
  border-radius: 12px;
  margin: 0;
  max-width: calc(100% - 40px);
  min-width: 0;
  overflow: hidden;
  padding: 10px 12px;
  white-space: pre-wrap;
  word-break: break-word;

  :deep(p) {
    margin: 0 0 0.65em;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

#app .chatbot-markdown,
.chatbot-element .chatbot-markdown {
  white-space: normal;

  :deep(> *:first-child) {
    margin-top: 0;
  }

  :deep(> *:last-child) {
    margin-bottom: 0;
  }

  :deep(p) {
    margin: 0 0 0.65em;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(ul),
  :deep(ol) {
    margin: 0 0 0.65em;
    padding-left: 1.25em;
  }

  :deep(li) {
    margin: 0.15em 0;
  }

  :deep(strong) {
    font-weight: 700;
  }

  :deep(a) {
    color: inherit;
    text-decoration: underline;
  }

  :deep(a[target="_blank"])::after {
    background-color: currentColor;
    content: '';
    display: inline-block;
    height: 0.85em;
    margin-left: 0.25em;
    -webkit-mask: var(--chatbot-external-link-icon) no-repeat center / contain;
    mask: var(--chatbot-external-link-icon) no-repeat center / contain;
    pointer-events: none;
    vertical-align: -0.1em;
    width: 0.85em;
  }

  :deep(hr) {
    background: transparent;
    border: 0;
    border-top: 1px solid rgba($haiti, 0.18);
    height: 0;
    margin: 0.85em 0;
  }

  :deep(code) {
    background-color: rgba($haiti, 0.08);
    border-radius: 4px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.9em;
    padding: 0.1em 0.35em;
  }

  :deep(pre) {
    background-color: rgba($haiti, 0.08);
    border-radius: 8px;
    margin: 0 0 0.65em;
    overflow-x: auto;
    padding: 10px 12px;

    code {
      background: transparent;
      padding: 0;
    }
  }

  :deep(blockquote) {
    border-left: 3px solid rgba($haiti, 0.25);
    margin: 0 0 0.65em;
    padding-left: 0.75em;
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4) {
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    margin: 0.85em 0 0.45em;
  }

  :deep(table) {
    border-collapse: collapse;
    border-spacing: 0;
    margin: 0;
    width: max-content;
  }

  :deep(.chatbot-md-table) {
    margin: 0.5em 0 0.75em;
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  :deep(thead) {
    background-color: rgba($haiti, 0.06);
  }

  :deep(th),
  :deep(td) {
    border: 1px solid rgba($haiti, 0.18);
    padding: 6px 10px;
    text-align: left;
    vertical-align: top;
    white-space: nowrap;
  }

  :deep(th) {
    font-weight: 700;
  }

  :deep(tbody tr:nth-child(even) td) {
    background-color: rgba($haiti, 0.03);
  }
}

.chatbot-message--user .chatbot-message-bubble {
  white-space: pre-wrap;
}

.chatbot-message--assistant .chatbot-markdown {
  :deep(code) {
    background-color: rgba($haiti, 0.06);
  }

  :deep(pre) {
    background-color: rgba($haiti, 0.06);
  }

  :deep(hr) {
    border-top-color: rgba($haiti, 0.16);
  }

  :deep(th),
  :deep(td) {
    border-color: rgba($haiti, 0.16);
  }
}

.chatbot-typing {
  align-items: center;
  display: inline-flex;
  gap: 8px;
  min-height: 18px;
}

.chatbot-typing-label {
  color: inherit;
  opacity: 0.75;
}

.chatbot-typing-dots {
  align-items: center;
  display: inline-flex;
  gap: 4px;
}

.chatbot-typing-dot {
  animation: chatbot-typing-bounce 1.2s ease-in-out infinite;
  background-color: currentColor;
  border-radius: 50%;
  height: 6px;
  opacity: 0.35;
  width: 6px;

  &:nth-child(2) {
    animation-delay: 0.15s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }
}

@keyframes chatbot-typing-bounce {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: translateY(0);
  }

  40% {
    opacity: 0.9;
    transform: translateY(-3px);
  }
}

.chatbot-composer {
  padding: 8px 16px 12px;
}

.chatbot-composer-field {
  position: relative;
}

.chatbot-settings-menu {
  background-color: $white;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  left: 0;
  overflow-y: auto;
  padding: 12px;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 3;

  &--sidebar {
    align-self: stretch;
    border-left: 1px solid $greyscale-semi-light;
    bottom: auto;
    flex-shrink: 0;
    left: auto;
    position: relative;
    right: auto;
    top: auto;
    width: 300px;
  }
}

.chatbot-settings-menu-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.chatbot-settings-menu-title {
  align-items: center;
  display: flex;
  gap: 8px;

  :deep(svg) {
    --color: #{$haiti};
  }
}

.chatbot-settings-section {
  display: flex;
  flex-direction: column;
  gap: 10px;

  :deep(.toggle) {
    align-items: flex-start;
    width: 100%;
  }

  :deep(.toggle-label) {
    font-size: inherit;
    line-height: inherit;
  }
}

.chatbot-settings-section-title {
  color: $haiti;
}

.chatbot-settings-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.chatbot-settings-field-label {
  align-items: center;
  color: $greyscale-medium;
  display: flex;
  gap: 6px;

  :deep(svg) {
    --color: #{$greyscale-medium};
  }
}

.chatbot-settings-select,
.chatbot-settings-input {
  appearance: none;
  background-color: $white;
  border: 1px solid $greyscale-semi-light;
  border-radius: 8px;
  color: $haiti;
  padding: 8px 10px;
  width: 100%;

  &:hover,
  &:focus {
    border-color: $cornflower-blue;
    outline: none;
  }
}

.chatbot-settings-api-key {
  align-items: center;
  display: flex;
  gap: 8px;
}

.chatbot-settings-api-key-field {
  flex: 1;
  min-width: 0;
  position: relative;

  .chatbot-settings-input {
    padding-right: 36px;
  }

  &:hover .chatbot-settings-api-key-reveal,
  &:focus-within .chatbot-settings-api-key-reveal {
    opacity: 1;
    pointer-events: auto;
  }
}

.chatbot-settings-api-key-reveal {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  display: flex;
  height: 28px;
  justify-content: center;
  opacity: 0;
  padding: 0;
  pointer-events: none;
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;

  :deep(svg) {
    --color: #{$greyscale-medium};
  }

  &:hover :deep(svg) {
    --color: #{$haiti};
  }

  &--visible {
    opacity: 1;
    pointer-events: auto;
  }
}

.chatbot-settings-api-key-save {
  background-color: $white;
  border: 1px solid $greyscale-semi-light;
  border-radius: 8px;
  color: $haiti;
  cursor: pointer;
  flex-shrink: 0;
  height: 32px;
  padding: 0 12px;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: $cornflower-blue;
    color: $cornflower-blue;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.chatbot-settings-select {
  cursor: pointer;
}

.chatbot-settings-size-options {
  display: flex;
  gap: 6px;
}

.chatbot-settings-size-option {
  background-color: $white;
  border: 1px solid $greyscale-semi-light;
  border-radius: 8px;
  color: $haiti;
  cursor: pointer;
  flex: 1;
  height: 32px;

  &:hover {
    border-color: $cornflower-blue;
    color: $cornflower-blue;
  }

  &--active {
    background-color: $low-grey;
    border-color: $cornflower-blue;
    color: $cornflower-blue;
  }
}

#app textarea.chatbot-composer-input,
.chatbot-element textarea.chatbot-composer-input {
  border: 1px solid $greyscale-semi-light;
  border-radius: 12px;
  color: $haiti;
  display: block;
  font-family: inherit;
  font-size: inherit;
  letter-spacing: normal;
  line-height: inherit;
  min-height: 48px;
  outline: none;
  padding: 12px 88px 12px 14px;
  resize: none;
  width: 100%;

  &:hover,
  &:focus {
    border: 1px solid $cornflower-blue;
    outline: none;
  }

  &:disabled {
    background-color: $low-grey;
    cursor: not-allowed;
  }
}

.chatbot-composer-actions {
  align-items: center;
  bottom: 8px;
  display: flex;
  gap: 6px;
  position: absolute;
  right: 8px;
}

.chatbot-composer-settings {
  align-items: center;
  background-color: $white;
  border: 1px solid $greyscale-semi-light;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;

  :deep(svg) {
    --color: #{$haiti};
  }

  &:hover {
    border-color: $cornflower-blue;

    :deep(svg) {
      --color: #{$cornflower-blue};
    }
  }

  &--active {
    background-color: $low-grey;
    border-color: $cornflower-blue;

    :deep(svg) {
      --color: #{$cornflower-blue};
    }
  }
}

.chatbot-composer-submit {
  align-items: center;
  background-color: $cornflower-blue;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  height: 32px;
  justify-content: center;
  position: static;
  width: 32px;

  :deep(svg) {
    --color: #{$white};
  }

  &:hover:not(:disabled) {
    background-color: $han-blue;
  }

  &:disabled {
    background-color: $low-grey;
    cursor: not-allowed;

    :deep(svg) {
      --color: #{$greyscale-medium};
    }
  }

  &--stop {
    background-color: $white;
    border: 1px solid $cornflower-blue;

    :deep(svg) {
      --color: #{$cornflower-blue};
    }

    &:hover:not(:disabled) {
      background-color: $low-grey;
    }
  }
}

.chatbot-message-raw {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

@media (max-width: 640px) {
  .chatbot-element {
    bottom: 16px;
    right: 16px;
  }

  .chatbot-panel {
    height: calc(100vh - 100px - #{$app-header-height});
    max-height: none;
    right: 0;
    width: calc(100vw - 32px);

    &--expanded {
      border-radius: 0;
      height: 100%;
      left: 0;
      max-width: none;
      top: 0;
      transform: none;
      width: 100%;
    }
  }

  .chatbot-settings-menu--sidebar {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: auto;
    z-index: 4;
  }

  .chatbot-panel--expanded .chatbot-messages,
  .chatbot-panel--expanded .chatbot-composer {
    max-width: none;
    padding-left: 16px;
    padding-right: 16px;
  }
}
</style>

<i18n src="@/locales/components/elements/chatbot-element.json"></i18n>
